/**
 * FinderToolbar Component
 *
 * Navigation arrows, breadcrumb, and view mode toggles.
 */

import { ViewMode } from './FinderWindow'
import './finder.css'

interface BreadcrumbItem {
  id: string
  name: string
}

interface FinderToolbarProps {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  canGoBack: boolean
  canGoForward: boolean
  onNavigateBack: () => void
  onNavigateForward: () => void
  breadcrumb: BreadcrumbItem[]
  onBreadcrumbClick?: (id: string) => void
}

export function FinderToolbar({
  viewMode,
  onViewModeChange,
  canGoBack,
  canGoForward,
  onNavigateBack,
  onNavigateForward,
  breadcrumb,
  onBreadcrumbClick,
}: FinderToolbarProps) {
  return (
    <div className="finder-toolbar">
      {/* Navigation arrows */}
      <div className="finder-toolbar__nav">
        <button
          className="finder-toolbar__nav-btn"
          onClick={onNavigateBack}
          disabled={!canGoBack}
          aria-label="Go back"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M8 1L3 6l5 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </button>
        <button
          className="finder-toolbar__nav-btn"
          onClick={onNavigateForward}
          disabled={!canGoForward}
          aria-label="Go forward"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M4 1l5 5-5 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </button>
      </div>

      {/* Breadcrumb */}
      <div className="finder-toolbar__breadcrumb">
        {breadcrumb.map((crumb, index) => (
          <span key={crumb.id} className="finder-toolbar__crumb">
            {index > 0 && <span className="finder-toolbar__crumb-sep">/</span>}
            <button
              className="finder-toolbar__crumb-btn"
              onClick={() => onBreadcrumbClick?.(crumb.id)}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      {/* View mode toggles */}
      <div className="finder-toolbar__views">
        <button
          className={`finder-toolbar__view-btn ${viewMode === 'icon' ? 'active' : ''}`}
          onClick={() => onViewModeChange('icon')}
          aria-label="Icon view"
          title="Icon view"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <rect x="1" y="1" width="5" height="5" rx="1" />
            <rect x="8" y="1" width="5" height="5" rx="1" />
            <rect x="1" y="8" width="5" height="5" rx="1" />
            <rect x="8" y="8" width="5" height="5" rx="1" />
          </svg>
        </button>
        <button
          className={`finder-toolbar__view-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => onViewModeChange('list')}
          aria-label="List view"
          title="List view"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <rect x="1" y="2" width="12" height="2" rx="0.5" />
            <rect x="1" y="6" width="12" height="2" rx="0.5" />
            <rect x="1" y="10" width="12" height="2" rx="0.5" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default FinderToolbar
